import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Radio,
  Button,
  Alert,
  Space,
  Tag,
  message,
  Spin,
  Modal,
  Form,
  Input,
} from "antd";
import {
  CreditCardOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  LockOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripePromise } from '../utils/stripe';
import { StripeService } from "../services/stripeService";
import { BillingService } from "../services/billingService";
import apiClient from "../utils/axios";

const { Title, Text } = Typography;

interface SubscriptionData {
  organization_id: string;
  organization_name: string;
  current_facility_count: number;
  active_facility_count: number;
  max_facilities: number;
  billing_cycle: 'Monthly' | 'Annual';
  subscription_status: string;
  is_free_trial: boolean;
  free_trial_expires_at?: string;
  next_billing_date?: string;
  monthly_cost: number;
  annual_cost: number;
  monthly_cost_per_facility: number;
  annual_monthly_cost_per_facility: number;
  setup_fee_per_facility: number;
}

interface SubscriptionSetupProps {
  organizationId: string;
  subscriptionData: SubscriptionData;
  onSubscriptionSuccess?: () => void;
  showTitle?: boolean;
  embedded?: boolean;
}

// Stripe Elements styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      padding: '10px 12px',
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: false,
};

// Payment form component
const PaymentForm: React.FC<{
  organizationId: string;
  billingCycle: 'monthly' | 'annual';
  subscriptionData: SubscriptionData;
  onSuccess: () => void;
  onCancel: () => void;
}> = ({ organizationId, billingCycle, subscriptionData, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { getAccessTokenSilently, user } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: user?.email || '',
  });

  // Update email when user data is available
  useEffect(() => {
    console.log('User email effect:', { userEmail: user?.email, currentEmail: customerInfo.email });
    if (user?.email && !customerInfo.email) {
      console.log('Setting email from user:', user.email);
      setCustomerInfo(prev => ({ ...prev, email: user.email || '' }));
    }
  }, [user?.email, customerInfo.email]);

  const handleSubmit = async () => {
    console.log('Payment submission started');

    if (!stripe || !elements) {
      console.error('Stripe or elements not available');
      message.error('Payment system not ready. Please try again.');
      return;
    }

    if (!customerInfo.name.trim()) {
      message.error('Please enter your full name');
      return;
    }

    if (!customerInfo.email.trim()) {
      message.error('Email address is required');
      return;
    }

    console.log('Submitting payment with:', { 
      name: customerInfo.name, 
      email: customerInfo.email,
      billingCycle 
    });

    setLoading(true);

    try {
      const token = await getAccessTokenSilently();
      
      // Create subscription with payment method
      const response = await apiClient.post(
        `/api/organization/${organizationId}/stripe/create-subscription-with-payment`,
        {
          billing_cycle: billingCycle,
          customer_name: customerInfo.name,
          customer_email: customerInfo.email,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { client_secret, setup_intent_id, is_trial } = response.data;

      console.log('Setup intent created:', { setup_intent_id, is_trial, client_secret });

      // Confirm the setup intent to collect payment method
      const { error: setupError, setupIntent } = await stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card: elements.getElement(CardElement)!,
          billing_details: {
            name: customerInfo.name,
            email: customerInfo.email,
          },
        },
      });

      if (setupError) {
        throw new Error(setupError.message);
      }

      if (setupIntent?.status !== 'succeeded') {
        throw new Error(`Payment method setup failed with status: ${setupIntent?.status}`);
      }

      console.log('Payment method setup successful, completing subscription...');

      // Complete the subscription creation
      const subscriptionResponse = await apiClient.post(
        `/api/organization/${organizationId}/stripe/complete-subscription`,
        {
          setup_intent_id: setup_intent_id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log('Subscription completed:', subscriptionResponse.data);

      if (is_trial) {
        message.success('Subscription created successfully! Your 14-day free trial has started.');
      } else {
        message.success('Subscription created successfully!');
      }
      
      onSuccess();

    } catch (error: any) {
      console.error('Payment failed:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      let errorMessage = 'Payment failed. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
          Full Name <span style={{ color: '#ff4d4f' }}>*</span>
        </label>
        <Input
          value={customerInfo.name}
          onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Enter your full name"
          size="large"
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
          Email Address
        </label>
        <Input
          value={customerInfo.email}
          disabled
          size="large"
          style={{ backgroundColor: '#f5f5f5', color: '#666' }}
        />
        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
          Using your account email address
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
          Card Information <span style={{ color: '#ff4d4f' }}>*</span>
        </label>
        <div style={{ 
          border: '1px solid #d9d9d9', 
          borderRadius: '6px', 
          padding: '11px 12px',
          backgroundColor: '#fff'
        }}>
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Alert
          message={
            <Space>
              <LockOutlined />
              <Text>Your payment information is secure and encrypted</Text>
            </Space>
          }
          type="info"
          showIcon={false}
          style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}
        />
      </div>

      <div style={{ textAlign: 'right' }}>
        <Space>
          <Button onClick={onCancel} size="large">
            Cancel
          </Button>
          <Button 
            type="primary" 
            onClick={handleSubmit}
            loading={loading}
            disabled={!stripe || !customerInfo.name.trim()}
            size="large"
            icon={loading ? null : <CreditCardOutlined />}
          >
            {loading ? 'Processing...' : `Subscribe ${billingCycle === 'monthly' ? 'Monthly' : 'Annually'}`}
          </Button>
        </Space>
      </div>
    </div>
  );
};

export const SubscriptionSetup: React.FC<SubscriptionSetupProps> = ({
  organizationId,
  subscriptionData,
  onSubscriptionSuccess,
  showTitle = true,
  embedded = false,
}) => {
  const [selectedBillingMode, setSelectedBillingMode] = useState<'CreditCard' | 'Invoice'>('CreditCard');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [processingInvoice, setProcessingInvoice] = useState(false);
  const { getAccessTokenSilently } = useAuth0();

  const calculateAnnualSavingsPercentage = () => {
    if (!subscriptionData) return 0;
    
    const monthlyAnnualCost = subscriptionData.monthly_cost_per_facility * 12;
    const actualAnnualCost = subscriptionData.annual_monthly_cost_per_facility * 12;
    const savings = monthlyAnnualCost - actualAnnualCost;
    const savingsPercentage = (savings / monthlyAnnualCost) * 100;
    
    return Math.round(savingsPercentage);
  };

  const handleCreditCardSubscription = (billingCycle: 'monthly' | 'annual') => {
    setSelectedBillingCycle(billingCycle);
    setShowPaymentModal(true);
  };

  const handleInvoiceSubscription = async (billingCycle: 'monthly' | 'annual') => {
    try {
      setProcessingInvoice(true);
      const token = await getAccessTokenSilently();
      
      await apiClient.post(
        `/api/organization/${organizationId}/stripe/create-invoice-subscription`,
        {
          billing_cycle: billingCycle,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success('Invoice subscription created successfully! You will receive invoices via email.');
      if (onSubscriptionSuccess) {
        onSubscriptionSuccess();
      }
      
    } catch (error: any) {
      console.error("Error creating invoice subscription:", error);
      message.error(error.response?.data?.detail || "Failed to create invoice subscription");
    } finally {
      setProcessingInvoice(false);
    }
  };

  const handleSubscriptionClick = (billingCycle: 'monthly' | 'annual') => {
    if (selectedBillingMode === 'CreditCard') {
      handleCreditCardSubscription(billingCycle);
    } else {
      handleInvoiceSubscription(billingCycle);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    if (onSubscriptionSuccess) {
      onSubscriptionSuccess();
    }
  };

  if (!subscriptionData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {showTitle && (
        <Title level={4} style={{ marginBottom: 16 }}>
          <Space>
            <ShoppingCartOutlined />
            Subscribe
          </Space>
        </Title>
      )}
      
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Start Your Subscription"
          description={`${subscriptionData.active_facility_count} active ${subscriptionData.active_facility_count === 1 ? 'facility' : 'facilities'}. Choose your billing cycle and payment method.`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      </div>

      {/* Billing Method Selection */}
      <div style={{ marginBottom: 24 }}>
        <Text strong style={{ marginBottom: 12, display: 'block' }}>Payment Method:</Text>
        <Radio.Group 
          value={selectedBillingMode} 
          onChange={(e) => setSelectedBillingMode(e.target.value)}
          style={{ width: '100%' }}
        >
          <Radio.Button value="CreditCard" style={{ width: '50%', textAlign: 'center' }}>
            <Space>
              <CreditCardOutlined />
              Credit Card
            </Space>
          </Radio.Button>
          <Radio.Button value="Invoice" style={{ width: '50%', textAlign: 'center' }}>
            <Space>
              <FileTextOutlined />
              Invoice (NET-30)
            </Space>
          </Radio.Button>
        </Radio.Group>
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">
            {selectedBillingMode === 'CreditCard' 
              ? 'Secure payment with immediate activation'
              : 'NET-30 invoice billing via email'
            }
          </Text>
        </div>
      </div>
      
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Card 
            size="small" 
            title="Monthly Billing"
            hoverable
            style={{ textAlign: 'center' }}
            actions={[
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button 
                  type="primary" 
                  size="large"
                  loading={processingInvoice && selectedBillingMode === 'Invoice'}
                  onClick={() => handleSubscriptionClick('monthly')}
                  icon={selectedBillingMode === 'CreditCard' ? <CreditCardOutlined /> : <FileTextOutlined />}
                >
                  Subscribe Monthly
                </Button>
              </Space>
            ]}
          >
            <div style={{ padding: '16px 0' }}>
              <Text strong style={{ fontSize: '24px', color: '#1890ff' }}>
                ${subscriptionData.monthly_cost_per_facility.toLocaleString()}/mo
              </Text>
              <div style={{ marginTop: 8 }}>
                per facility
              </div>
              <div style={{ marginTop: 8, height: '22px' }}>
                {/* Spacer to match Annual Billing card height */}
              </div>
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">
                  Setup fee: ${(subscriptionData.setup_fee_per_facility * subscriptionData.active_facility_count).toLocaleString()} 
                  {subscriptionData.is_free_trial ? ' (charged when trial ends)' : ' (one-time)'}
                </Text>
              </div>
              <div style={{ marginTop: 8 }}>
                <Text strong>
                  {subscriptionData.is_free_trial 
                    ? `Monthly rate: $${(subscriptionData.monthly_cost_per_facility * subscriptionData.active_facility_count).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/month after trial`
                    : `Total: $${((subscriptionData.monthly_cost_per_facility * subscriptionData.active_facility_count) + (subscriptionData.setup_fee_per_facility * subscriptionData.active_facility_count)).toLocaleString()}/first month`
                  }
                </Text>
              </div>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} md={12}>
          <Card 
            size="small" 
            title="Annual Billing"
            hoverable
            style={{ textAlign: 'center' }}
            actions={[
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button 
                  type="primary" 
                  size="large"
                  loading={processingInvoice && selectedBillingMode === 'Invoice'}
                  onClick={() => handleSubscriptionClick('annual')}
                  icon={selectedBillingMode === 'CreditCard' ? <CreditCardOutlined /> : <FileTextOutlined />}
                >
                  Subscribe Annually
                </Button>
              </Space>
            ]}
          >
            <div style={{ padding: '16px 0' }}>
              <Text strong style={{ fontSize: '24px', color: '#52c41a' }}>
                ${(subscriptionData.annual_monthly_cost_per_facility * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/yr
              </Text>
              <div style={{ marginTop: 8 }}>
                per facility (billed annually)
              </div>
              <div style={{ marginTop: 8, height: '22px' }}>
                {calculateAnnualSavingsPercentage() > 0 && (
                  <Tag color="green">Save {calculateAnnualSavingsPercentage()}%</Tag>
                )}
              </div>
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">
                  Setup fee: ${(subscriptionData.setup_fee_per_facility * subscriptionData.active_facility_count).toLocaleString()} 
                  {subscriptionData.is_free_trial ? ' (charged when trial ends)' : ' (one-time)'}
                </Text>
              </div>
              <div style={{ marginTop: 8 }}>
                <Text strong>
                  {subscriptionData.is_free_trial 
                    ? `Monthly rate: $${(subscriptionData.annual_monthly_cost_per_facility * subscriptionData.active_facility_count).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/month after trial`
                    : `Total: $${((subscriptionData.annual_monthly_cost_per_facility * subscriptionData.active_facility_count * 12) + (subscriptionData.setup_fee_per_facility * subscriptionData.active_facility_count)).toLocaleString()}/first year`
                  }
                </Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Credit Card Payment Modal */}
      <Modal
        title={
          <Space>
            <CreditCardOutlined />
            <span>Secure Payment</span>
          </Space>
        }
        open={showPaymentModal}
        onCancel={() => setShowPaymentModal(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Elements stripe={stripePromise}>
          <PaymentForm
            organizationId={organizationId}
            billingCycle={selectedBillingCycle}
            subscriptionData={subscriptionData}
            onSuccess={handlePaymentSuccess}
            onCancel={() => setShowPaymentModal(false)}
          />
        </Elements>
      </Modal>
    </div>
  );
}; 